/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component, so the PascalCase-for-components rule does not apply.
   Matches the same exemption on table-internals/use-row-keyboard-navigation.ts. */
import {
  type ColumnFiltersState,
  type ExpandedState,
  type OnChangeFn,
  type RowData,
  type SortingState,
  useTable,
} from '@tanstack/react-table';
import * as React from 'react';

import { readOwnFlag } from '../table-internals/read-own-flag.js';
import type {
  DimeNestedTableProps,
  ParentColumnDef,
  ParentRow,
  VisibilityState,
} from './DimeNestedTable.types.js';
import { buildExpandColumn } from './expand-column.js';
import { dimeNestedTableFeatures, type DimeNestedTableFeatures } from './nested-table-features.js';
import { readCachedChildren } from './read-cached-children.js';
import { shouldReleaseRequest } from './should-release-request.js';
import { useLatestFetchState } from './use-latest-fetch-state.js';
import { useReleaseResetRequests } from './use-release-reset-requests.js';
import { useRequestedParentIds } from './use-requested-parent-ids.js';

type NestedParentTableOptions<TParent extends RowData, TChild> = Pick<
  DimeNestedTableProps<TParent, TChild>,
  | 'childRowsByParentId'
  | 'columnFilters'
  | 'columns'
  | 'columnVisibility'
  | 'data'
  | 'expanded'
  | 'getRowCanExpand'
  | 'getRowId'
  | 'isLoading'
  | 'loadingParentIds'
  | 'manualFiltering'
  | 'manualSorting'
  | 'onColumnFiltersChange'
  | 'onColumnVisibilityChange'
  | 'onExpandedChange'
  | 'onExpandRow'
  | 'onUpdateSortBy'
  | 'sortBy'
>;

/** Builds the parent TanStack table: controlled-with-uncontrolled-fallback state for expansion,
 *  sorting, filtering and visibility, plus the lazy-fetch expand handler and the expand column. */
export function useNestedParentTable<TParent extends RowData, TChild>({
  columns,
  data,
  getRowId,
  expanded,
  onExpandedChange,
  onExpandRow,
  sortBy,
  onUpdateSortBy,
  manualSorting = false,
  columnFilters,
  onColumnFiltersChange,
  manualFiltering = false,
  columnVisibility,
  onColumnVisibilityChange,
  childRowsByParentId,
  loadingParentIds,
  getRowCanExpand,
  isLoading = false,
}: NestedParentTableOptions<TParent, TChild>) {
  const [internalExpanded, setInternalExpanded] = React.useState<Record<string, boolean>>({});
  const resolvedExpanded = expanded ?? internalExpanded;
  const resolvedOnExpandedChange = onExpandedChange ?? setInternalExpanded;

  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const resolvedSorting = sortBy ?? internalSorting;
  const resolvedOnSortingChange = onUpdateSortBy ?? setInternalSorting;

  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([]);
  const resolvedColumnFilters = columnFilters ?? internalColumnFilters;
  const resolvedOnColumnFiltersChange = onColumnFiltersChange ?? setInternalColumnFilters;

  const [internalVisibility, setInternalVisibility] = React.useState<VisibilityState>({});
  const resolvedVisibility = columnVisibility ?? internalVisibility;
  const resolvedOnVisibilityChange = onColumnVisibilityChange ?? setInternalVisibility;

  const {
    isRequested,
    markRequested,
    clearRequested,
    hasConsumerReportedFetching,
    releaseUncachedRequests,
  } = useRequestedParentIds(loadingParentIds);

  const { readChildRows, readLoadingParentIds } = useLatestFetchState(
    childRowsByParentId,
    loadingParentIds,
  );

  useReleaseResetRequests(childRowsByParentId, releaseUncachedRequests);

  // These deps must reach the resolvedColumns memo below too, or the expand cell keeps a handler
  // closed over a stale cache and re-fires onExpandRow for parents whose children already arrived.
  const handleToggleExpand = React.useCallback(
    (row: ParentRow<TParent>) => {
      const willExpand = !row.getIsExpanded();
      row.toggleExpanded();
      // A fetch already in flight must not be issued twice: collapsing and re-expanding before it
      // resolves leaves the cache undefined, which reads as "never fetched". Our own record covers
      // consumers who never wired loadingParentIds; theirs permits a retry after a failure, which
      // never writes the cache — but only on a true -> false transition we actually observed.
      const currentChildRows = readChildRows();
      const currentLoadingParentIds = readLoadingParentIds();
      const isCached = readCachedChildren(currentChildRows, row.id) !== undefined;
      const consumerReportsFetching = readOwnFlag(currentLoadingParentIds, row.id);
      if (
        shouldReleaseRequest({
          childRowsByParentId: currentChildRows,
          loadingParentIds: currentLoadingParentIds,
          parentId: row.id,
          hasConsumerReportedFetching,
        })
      ) {
        clearRequested(row.id);
      }
      const isFetching = consumerReportsFetching || isRequested(row.id);
      if (willExpand && !isCached && !isFetching) {
        // Scoped to this request: the callback may be held and called after a later expand has
        // started a new one, and releasing that would let a duplicate fetch through.
        const generation = markRequested(row.id);
        onExpandRow?.(row.original, () => clearRequested(row.id, generation));
      }
    },
    [
      readChildRows,
      readLoadingParentIds,
      onExpandRow,
      isRequested,
      markRequested,
      clearRequested,
      hasConsumerReportedFetching,
    ],
  );

  const resolvedColumns: ParentColumnDef<TParent>[] = React.useMemo(
    () => [buildExpandColumn<TParent>(handleToggleExpand), ...columns],
    [columns, handleToggleExpand],
  );

  const table = useTable({
    features: dimeNestedTableFeatures,
    data,
    columns: resolvedColumns as unknown as Parameters<
      typeof useTable<DimeNestedTableFeatures, TParent>
    >[0]['columns'],
    getRowId: (row) => getRowId(row as TParent),
    state: {
      expanded: resolvedExpanded,
      sorting: resolvedSorting,
      columnFilters: resolvedColumnFilters,
      columnVisibility: resolvedVisibility,
    },
    // ExpandedState widens to `true`, which no prop here can produce (nothing calls
    // toggleAllRowsExpanded), so Record<string, boolean> is the only observable shape.
    onExpandedChange: resolvedOnExpandedChange as OnChangeFn<ExpandedState>,
    onSortingChange: resolvedOnSortingChange,
    onColumnFiltersChange: resolvedOnColumnFiltersChange,
    onColumnVisibilityChange: resolvedOnVisibilityChange,
    manualSorting,
    manualFiltering,
    // Without this, every data change schedules a reset that calls onExpandedChange({}) with no
    // user action behind it, wiping a controlled consumer's state on each page turn.
    manualExpanding: true,
    // A fetched [] is known-empty and suppressed; an absent entry is the lazy path and stays open.
    getRowCanExpand: (row) =>
      readCachedChildren(childRowsByParentId, row.id)?.length !== 0 &&
      (getRowCanExpand ? getRowCanExpand(row.original as TParent) : true),
    meta: { isLoading },
  });

  return {
    table,
    handleToggleExpand,
    resolvedOnExpandedChange,
    isRequested,
    markRequested,
    clearRequested,
    hasConsumerReportedFetching,
    readChildRows,
    readLoadingParentIds,
  };
}
